import React, {useCallback, useMemo, useRef} from 'react';
import {v4 as uuidv4} from 'uuid';

import {ProcedureParameterConfirmationMethod} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import {
  CreateStructuredTableProps,
  LastSyncedUpstreamValue,
  StructuredTableColumn,
  StructuredTableRow,
} from 'notebook/structured-tables/StructuredTableConstants';
import {EMPTY_PLACEHOLDER_INSERTION_CONTEXT} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import computeLSUVs from 'procedures/design/method-config/computeLSUVs';
import {ProcedureValidationContext} from 'procedures/design/validation/ProcedureValidationContext';
import {
  Method,
  MethodUpdater,
  ProcedureMaterialInputPlannedValue,
  ProcedureMaterialInputSetting,
  MaterialFlow,
} from 'procedures/ProcedureDomainTypes';
import {ProcedurePageContext} from 'procedures/ProcedurePageContext';
import {
  DEFAULT_MATERIAL_INPUT_TYPE,
  MaterialType,
} from 'procedures/shared-components/schema-field-type-selectors/procedureSchemaFieldConstants';
import {transformLsuvsForRowFn} from 'procedures/tables/parameterTableAdapterParamUsageUtils';
import ProcedureMaterialInputsTableAdapter, {
  ProcedureMaterialInputsTableMetadata,
  ProcedureMaterialInputsTableRowMetadata,
} from 'procedures/tables/ProcedureMaterialInputsTableAdapter';
import useRowDelegateController from 'procedures/tables/useRowDelegateController';
import {useAllTagSchemasWithStandardShape} from 'procedures/utils/useAllTagSchemas';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import {SchemaInterface} from 'schemas/SchemaConstants';
import {useAllSchemaInterfacesQuery} from 'schemas/util/useAllSchemaInterfacesQuery';

import ProcedureConfigTable from './ProcedureConfigTable';

const baseCreateMaterialInputsTableProps: Omit<
  CreateStructuredTableProps<ProcedureMaterialInputsTableMetadata>,
  'metadata'
> = {
  name: 'Material inputs',
  schemas: [],
  entryId: null,
  selectorsById: {},
  structuredTableInsertionContext: EMPTY_PLACEHOLDER_INSERTION_CONTEXT,
};

interface MaterialInputConfigTableProps {
  method: Method;
  onChangeMethod: (cid: string, methodUpdater: MethodUpdater) => void;
  onChangeErrorState: (anyErrors: boolean) => void;
  onReadyForEditing: () => void;
  locked: boolean;
  navigateToStep: (stepCid: string) => void;
  materialFlows: Array<MaterialFlow>;
}

export function makeEmptyMaterialInputPlannedValue(cid: string): ProcedureMaterialInputPlannedValue {
  return {
    id: null,
    cid,
    amount: null,
    material: {
      type: FieldType.TEXT,
      value: null,
    },
  };
}

function makeEmptyMaterialInputSetting(): ProcedureMaterialInputSetting {
  const cid = uuidv4();
  const valueCid = `${cid}-planned-value-0`;
  return {
    materialInputVersion: {
      cid,
      id: null,
      procedureMaterialInputId: null,
      name: '',
      description: '',
      dataType: DEFAULT_MATERIAL_INPUT_TYPE,
      amountUnit: null,
      confirmationMethod: ProcedureParameterConfirmationMethod.TABLE,
    },
    values: [makeEmptyMaterialInputPlannedValue(valueCid)],
  };
}

export default function MaterialInputConfigTable(props: MaterialInputConfigTableProps): JSX.Element {
  const {method, onChangeMethod, onReadyForEditing, materialFlows} = props;
  const {procedureValidationErrors} = ProcedureValidationContext.useContext();
  const {isProcedurePoW} = ProcedurePageContext.useContext();
  const adapter = ProcedureMaterialInputsTableAdapter.getAdapter();
  const tagSchemas = useAllTagSchemasWithStandardShape(onReadyForEditing);
  const schemaInterfacesResult = useAllSchemaInterfacesQuery();
  // null while loading so fieldset hydration is skipped silently rather than erroring.
  const schemaInterfaces: Array<SchemaInterface> | null = useMemo(
    () =>
      schemaInterfacesResult.type === QueryResultType.SUCCESS
        ? schemaInterfacesResult.data.schemaInterfaces
        : null,
    [schemaInterfacesResult]
  );
  const lsuvs = useRef<Record<string, Array<LastSyncedUpstreamValue>> | null>(null);
  const createMaterialInputsTableProps = useMemo(
    () => ({...baseCreateMaterialInputsTableProps, metadata: {navigateToStep: props.navigateToStep}}),
    [props.navigateToStep]
  );

  const rowDelegateController = useRowDelegateController<
    ProcedureMaterialInputSetting,
    ProcedureMaterialInputsTableMetadata,
    ProcedureMaterialInputsTableRowMetadata,
    MaterialType
  >({
    dependentColumnProvenanceIds: 'ALL_DELEGATE',
    details: method.materialInputSettings,
    getDataTypeByIndex: (index) =>
      method.materialInputSettings[index]?.materialInputVersion.dataType ?? DEFAULT_MATERIAL_INPUT_TYPE,
    createTable: (installDelegatesIntoRow) => {
      const materialInputs =
        method.materialInputSettings.length > 0
          ? method.materialInputSettings
          : [makeEmptyMaterialInputSetting()];

      const table = adapter.createTableFromMaterials(
        createMaterialInputsTableProps,
        `procedureMaterialInputTable-${method.cid}`,
        materialInputs,
        materialFlows,
        procedureValidationErrors,
        method.parameterUsages,
        isProcedurePoW
      );

      const rowsWithDelegates = table.rows.map((row, index) => installDelegatesIntoRow(table, row, index));

      if (lsuvs.current == null) {
        lsuvs.current = computeLSUVs(
          rowsWithDelegates,
          table.columns,
          materialInputs,
          (mi) => mi.materialInputVersion.id,
          transformLsuvsForRowFn(
            (model, row, columns, parameterUsages) =>
              ProcedureMaterialInputsTableAdapter.makeLastSyncedUpstreamValues(
                model,
                row,
                columns,
                parameterUsages,
                isProcedurePoW
              ),
            method.parameterUsages,
            (model) => model.materialInputVersion.id
          )
        );
      }

      return {
        ...table,
        rows: table.rows.map((row, index) => {
          const materialInput = materialInputs[index];
          const id = materialInput.materialInputVersion.id;
          return {
            ...rowsWithDelegates[index],
            ...(id && lsuvs.current![id] ? {lastSyncedUpstreamValues: lsuvs.current![id]} : null),
            isFinalized: id != null,
          };
        }),
      };
    },
    getDelegateColumnByColumnAndDataType:
      ProcedureMaterialInputsTableAdapter.getDelegateColumnByColumnAndMaterialType,
  });

  const onChangeMaterialInputSettingsDetails = useCallback(
    (materialInputSettings: Array<ProcedureMaterialInputSetting>) => {
      onChangeMethod(method.cid, (m) => ({...m, materialInputSettings}));
      // The parent needs to flush before the row delegates can be refreshed
      setTimeout(() => {
        rowDelegateController.onChangeDetails(materialInputSettings);
      }, 0);
    },
    [method, onChangeMethod, rowDelegateController]
  );

  return (
    <ProcedureConfigTable
      tableAdapter={ProcedureMaterialInputsTableAdapter.getAdapter()}
      onChangeDetails={onChangeMaterialInputSettingsDetails}
      onChangeErrorState={props.onChangeErrorState}
      rowToDetail={(
        row: StructuredTableRow<ProcedureMaterialInputsTableRowMetadata>,
        columns: Array<StructuredTableColumn>
      ) =>
        ProcedureMaterialInputsTableAdapter.rowToMaterialInputSetting(
          columns,
          row,
          tagSchemas ?? [],
          schemaInterfaces
        )
      }
      table={rowDelegateController.table}
      tableCid={`procedureMaterialInputsTable-${method.cid}`}
      locked={props.locked}
      dataTestAttrs={{component: 'MaterialInputConfigTable', element: 'root'}}
    />
  );
}
